import { useAuth } from "../../auth/AuthContext";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CooperationSnapshot } from "../../components/CooperationSnapshot";
import { OrderStatusBadge } from "../../components/OrderStatusBadge";
import { ordersApi, type Order } from "../../api/orders";
import { contractsApi, type Contract } from "../../api/contracts";
import { financeApi, type FinanceDetail } from "../../api/finance";
import { fmtDate, money } from "../finance/money";

/**
 * Sales-side READ-ONLY "Customer view" of a single company: the customer's-eye
 * dashboard scoped by `companyId`. It shows the same Cooperation Snapshot the
 * customer sees, plus compact summaries of that company's Orders, Finance and
 * Contracts. No create/submit actions — Sales stays Sales and only
 * previews. Drill-in links point at the existing detail routes (which gate
 * their own actions). All reads are gated server-side by sales-assignment.
 */
export function CustomerDashboardView({
  companyId,
  companyName,
}: {
  companyId: string;
  companyName: string;
}) {
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();
  const canOrders = hasPermission("section:orders:read");
  const canContracts = hasPermission("section:sales_contracts:read");
  const canFinance = hasPermission("section:customer_finance:read");
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [contracts, setContracts] = useState<Contract[] | null>(null);
  const [finance, setFinance] = useState<FinanceDetail | null>(null);
  const [financeError, setFinanceError] = useState<string | null>(null);

  useEffect(() => {
    if (canOrders) void ordersApi.list({ companyId, size: 100 })
        .then((r) => setOrders(r.content))
        .catch(() => setOrders([]));
    if (canContracts) void contractsApi.list({ companyId }).then(setContracts).catch(() => setContracts([]));
    setFinance(null);
    setFinanceError(null);
    if (canFinance) financeApi
      .company(companyId)
      .then(setFinance)
      .catch((e) => setFinanceError((e as { message?: string }).message ?? "—"));
  }, [companyId, canOrders, canContracts, canFinance]);

  const lang = i18n.resolvedLanguage ?? i18n.language;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <p style={previewNote}>
        {t(
          "company.customerView.note",
          "Read-only preview of how {{company}} sees their portal. Use the Manage tab to make changes.",
          { company: companyName },
        )}
      </p>

      <CooperationSnapshot companyId={companyId} />

      {canFinance && <FinanceSummary data={finance} error={financeError} />}

      {canOrders && <section className="card">
        <header className="card-h">
          <h3>{t("company.customerView.orders", "Orders")}</h3>
          <Link to={`/orders?companyId=${companyId}`} style={actionLink}>
            {t("company.customerView.viewAll", "View all →")}
          </Link>
        </header>
        <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {orders === null ? (
            <Muted>{t("common.loading", "Loading…")}</Muted>
          ) : orders.length === 0 ? (
            <Empty>{t("company.customerView.noOrders", "No orders yet.")}</Empty>
          ) : (
            <ul style={list}>
              {orders.slice(0, 6).map((o) => (
                <li key={o.id} style={row}>
                  <Link to={`/orders/${o.id}`} style={rowLink}>
                    <code style={mono}>{o.orderNumber}</code>
                    <span style={dim}>
                      {/* Datum ide prvi: spisak je poređan po vremenu, pa je to ono po čemu se
                          red prepoznaje. Broj porudžbine ne kaže kad je nastala. */}
                      {fmtDate(o.submittedAt ?? o.createdAt)}
                      {" · "}
                      {t("company.customerView.lines", "{{count}} lines", { count: o.lines.length })}
                      {o.orderTotal ? ` · ${money(o.orderTotal, o.currency)}` : ""}
                    </span>
                  </Link>
                  <OrderStatusBadge status={o.status} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>}

      {canContracts && <section className="card">
        <header className="card-h">
          <h3>{t("company.customerView.contracts", "Contracts")}</h3>
        </header>
        <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {contracts === null ? (
            <Muted>{t("common.loading", "Loading…")}</Muted>
          ) : contracts.length === 0 ? (
            <Empty>{t("company.customerView.noContracts", "No contracts on file.")}</Empty>
          ) : (
            <ul style={list}>
              {contracts.slice(0, 6).map((c) => (
                <li key={c.id} style={row}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <b style={{ fontSize: 13, color: "var(--ink)" }}>{c.referenceNumber}</b>
                    <span style={dim}>
                      {c.title}
                      {c.effectiveDate
                        ? ` · ${new Date(c.effectiveDate).toLocaleDateString(lang)}`
                        : ""}
                    </span>
                  </span>
                  <span style={neutralChip}>{c.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>}
    </div>
  );
}

/** Compact finance card: balance / overdue / credit. Finance may be off for a
 * market (404/403) — show a muted note instead of an error banner. */
function FinanceSummary({ data, error }: { data: FinanceDetail | null; error: string | null }) {
  const { t } = useTranslation();
  if (error) {
    return (
      <section className="card">
        <header className="card-h">
          <h3>{t("company.customerView.finance", "Finance")}</h3>
        </header>
        <div className="card-b">
          <Empty>{t("company.customerView.financeUnavailable", "Finance summary is not available.")}</Empty>
        </div>
      </section>
    );
  }
  if (!data) {
    return (
      <section className="card">
        <header className="card-h">
          <h3>{t("company.customerView.finance", "Finance")}</h3>
        </header>
        <div className="card-b">
          <Muted>{t("common.loading", "Loading…")}</Muted>
        </div>
      </section>
    );
  }
  const cur = data.account.currency || "EUR";
  return (
    <section className="card">
      <header className="card-h">
        <h3>{t("company.customerView.finance", "Finance")}</h3>
      </header>
      <div className="card-b" style={financeGrid}>
        <FinTile label={t("finance.kpi.balance", "Balance")} value={money(data.account.balanceTotal, cur)} />
        <FinTile
          label={t("finance.kpi.overdue", "Overdue")}
          value={money(data.account.overdueTotal, cur)}
          warn={data.account.overdueTotal > 0}
          // Overdue is counted on the day the outgoing invoices reach, as on the card; named here too.
          note={data.asOf ? t("finance.asOf", "stanje na dan {{datum}}", { datum: fmtDate(data.asOf) }) : undefined}
        />
        <FinTile label={t("finance.kpi.creditLimit", "Credit limit")} value={money(data.account.creditLimit, cur)} />
        <FinTile label={t("finance.kpi.creditAvailable", "Credit available")} value={money(data.account.creditAvailable, cur)} />
      </div>
    </section>
  );
}

function FinTile({ label, value, warn, note }: { label: string; value: string; warn?: boolean; note?: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</span>
      <strong style={{ fontSize: 20, color: warn ? "var(--err)" : "var(--ink)", fontVariantNumeric: "tabular-nums" }}>{value}</strong>
      {note && <span style={{ fontSize: 11, color: "var(--ink-3)" }}>{note}</span>}
    </div>
  );
}

function Muted({ children }: { children: React.ReactNode }) {
  return <p style={{ color: "var(--ink-3)", fontSize: 13, margin: 0 }}>{children}</p>;
}
function Empty({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: "16px 12px", textAlign: "center", color: "var(--ink-4)", fontSize: 13, background: "var(--surface-2)", borderRadius: 8, border: "1px dashed var(--border)" }}>
      {children}
    </div>
  );
}

const previewNote: React.CSSProperties = {
  margin: 0, fontSize: 12.5, color: "var(--ink-3)", padding: "8px 12px",
  background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 8,
};
const list: React.CSSProperties = { listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 };
const row: React.CSSProperties = { display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: 8 };
const rowLink: React.CSSProperties = { flex: 1, minWidth: 0, color: "var(--ink)", textDecoration: "none", display: "flex", flexDirection: "column", gap: 2 };
const mono: React.CSSProperties = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12, color: "var(--ink)" };
const dim: React.CSSProperties = { color: "var(--ink-3)", fontSize: 12 };
const actionLink: React.CSSProperties = { fontSize: 12, color: "var(--brand-primary)", textDecoration: "none", fontWeight: 600 };
const neutralChip: React.CSSProperties = { fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: "var(--surface)", color: "var(--ink-2)", whiteSpace: "nowrap", border: "1px solid var(--border)" };
const financeGrid: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 16 };
